// 판 기억 — 여러 사람이 카드를 한 장씩 내는 장면을 지켜본 뒤 "누가 무엇을 냈나"에 답한다.
// 게임 판의 흐름을 머릿속에 붙잡아 두는 힘(작업 기억)을 훈련한다. 레벨이 오르면 사람·장수가 늘고 더 빨리 지나간다.
import { hasBatchim, int, makeRng, pick, shuffle, type Rng } from './rng';

export type CardColor = 'red' | 'blue';
export interface MemEvent { player: string; num: number; color: CardColor }
export interface MemQuestion { text: string; choices: string[]; answer: string }
export interface MemRound { players: string[]; events: MemEvent[]; showMs: number; colored: boolean; questions: MemQuestion[] }

const NAMES = ['민준', '서연', '지호', '하은'];
const COLOR_WORD: Record<CardColor, string> = { red: '빨강', blue: '파랑' };
export const NOBODY = '아무도 안 냄';

const iGa = (name: string) => `${name}${hasBatchim(name) ? '이' : '가'}`;
const eunNeun = (name: string) => `${name}${hasBatchim(name) ? '은' : '는'}`;

export function cardLabel(e: { num: number; color: CardColor }, colored: boolean): string {
  return colored ? `${COLOR_WORD[e.color]} ${e.num}` : `${e.num}`;
}

function config(level: number) {
  const lv = Math.min(10, Math.max(1, level));
  return {
    players: lv <= 2 ? 2 : lv <= 5 ? 3 : 4,
    events: 3 + lv,
    showMs: 2200 - 120 * lv,
    colored: lv >= 2,
    rotate: lv <= 4, // 낮은 레벨은 차례대로, 높은 레벨은 아무나 낸다
    questions: lv <= 3 ? 4 : 5,
  };
}

function numberChoices(rng: Rng, answer: number, near: number[]): string[] {
  const wrong = new Set<number>();
  for (const n of shuffle(rng, near)) if (n !== answer && n >= 0 && wrong.size < 3) wrong.add(n);
  for (let d = 1; wrong.size < 3; d++) {
    if (answer + d !== answer) wrong.add(answer + d);
    if (wrong.size < 3 && answer - d >= 0) wrong.add(answer - d);
  }
  return shuffle(rng, [String(answer), ...[...wrong].slice(0, 3).map(String)]);
}

function playerChoices(rng: Rng, players: string[], answer: string, withNobody: boolean): string[] {
  const others = shuffle(rng, players.filter((p) => p !== answer));
  const pool = withNobody && answer !== NOBODY ? [...others.slice(0, 2), NOBODY] : others.slice(0, 3);
  return shuffle(rng, [answer, ...pool]);
}

export function makeMemoryRound(level: number, seed: number): MemRound {
  const rng = makeRng(seed);
  const c = config(level);
  const players = NAMES.slice(0, c.players);
  const colors: CardColor[] = c.colored ? ['red', 'blue'] : ['red'];
  const events: MemEvent[] = [];
  const start = int(rng, 0, players.length - 1);
  for (let i = 0; i < c.events; i++) {
    const player = c.rotate || i < players.length ? players[(start + i) % players.length] : pick(rng, players);
    events.push({ player, num: int(rng, 1, 9), color: pick(rng, colors) });
  }
  const label = (e: MemEvent) => cardLabel(e, c.colored);
  const byPlayer = (p: string) => events.filter((e) => e.player === p);

  const makers: (() => MemQuestion | null)[] = [
    () => {
      const p = pick(rng, players);
      const sum = byPlayer(p).reduce((s, e) => s + e.num, 0);
      return { text: `${iGa(p)} 낸 숫자를 모두 더하면?`, answer: String(sum), choices: numberChoices(rng, sum, [sum + 1, sum - 1, sum + 2, sum - 2, ...players.filter((q) => q !== p).map((q) => byPlayer(q).reduce((s, e) => s + e.num, 0))]) };
    },
    () => {
      const unique = events.filter((e) => events.filter((x) => label(x) === label(e)).length === 1);
      if (rng() < 0.3 || unique.length === 0) { // 나오지 않은 카드 — 없던 것을 있었다고 착각하지 않는지
        const absent = shuffle(rng, colors.flatMap((color) => [1, 2, 3, 4, 5, 6, 7, 8, 9].map((num) => ({ num, color }))))
          .find((x) => !events.some((e) => label(e) === cardLabel(x, c.colored)));
        if (!absent) return null;
        return { text: `'${cardLabel(absent, c.colored)}' 카드를 낸 사람은?`, answer: NOBODY, choices: playerChoices(rng, players, NOBODY, true) };
      }
      const e = pick(rng, unique);
      return { text: `'${label(e)}' 카드를 낸 사람은?`, answer: e.player, choices: playerChoices(rng, players, e.player, true) };
    },
    () => {
      const i = int(rng, 0, events.length - 1);
      const answer = label(events[i]);
      const wrong = shuffle(rng, [...new Set(events.map(label))].filter((l) => l !== answer));
      while (wrong.length < 3) {
        const l = cardLabel({ num: int(rng, 1, 9), color: pick(rng, colors) }, c.colored);
        if (l !== answer && !wrong.includes(l)) wrong.push(l);
      }
      return { text: `${i + 1}번째로 나온 카드는?`, answer, choices: shuffle(rng, [answer, ...wrong.slice(0, 3)]) };
    },
    () => {
      const p = pick(rng, players);
      const mine = byPlayer(p);
      const answer = label(mine[mine.length - 1]);
      const wrong = shuffle(rng, [...new Set(events.map(label))].filter((l) => l !== answer)).slice(0, 3);
      while (wrong.length < 3) {
        const l = cardLabel({ num: int(rng, 1, 9), color: pick(rng, colors) }, c.colored);
        if (l !== answer && !wrong.includes(l)) wrong.push(l);
      }
      return { text: `${iGa(p)} 마지막으로 낸 카드는?`, answer, choices: shuffle(rng, [answer, ...wrong]) };
    },
    () => {
      if (!c.colored) return null;
      const color = pick(rng, colors);
      const n = events.filter((e) => e.color === color).length;
      return { text: `${COLOR_WORD[color]} 카드는 모두 몇 장 나왔나?`, answer: String(n), choices: numberChoices(rng, n, [n + 1, n - 1, n + 2]) };
    },
    () => {
      if (players.length < 3) return null;
      const max = Math.max(...events.map((e) => e.num));
      const who = [...new Set(events.filter((e) => e.num === max).map((e) => e.player))];
      if (who.length !== 1) return null;
      return { text: '가장 큰 숫자를 낸 사람은?', answer: who[0], choices: playerChoices(rng, players, who[0], false) };
    },
    () => {
      if (c.rotate) return null;
      const p = pick(rng, players);
      const n = byPlayer(p).length;
      return { text: `${eunNeun(p)} 카드를 몇 장 냈나?`, answer: String(n), choices: numberChoices(rng, n, [n + 1, n - 1, n + 2]) };
    },
  ];

  const questions: MemQuestion[] = [];
  for (let round = 0; round < 4 && questions.length < c.questions; round++) {
    for (const make of shuffle(rng, makers)) {
      if (questions.length >= c.questions) break;
      const q = make();
      if (q && !questions.some((x) => x.text === q.text)) questions.push(q);
    }
  }
  return { players, events, showMs: c.showMs, colored: c.colored, questions };
}
